import React, { useState } from 'react';
import { Button, Input, Label, Textarea, Select, Dialog, DialogHeader, DialogTitle, DialogContent, DialogFooter } from '@clinic-os/ui';
import { usePatients } from '../../hooks/usePatients';
import { DEMO_CLINIC_ID } from '../../data/demo-data';
import type { Appointment } from '@clinic-os/shared/types';

interface BookAppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  onBook: (data: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => void;
  defaultDate?: Date;
}

const APPOINTMENT_TYPES = [
  { value: 'checkup', label: 'Checkup' },
  { value: 'cleaning', label: 'Cleaning / Prophylaxis' },
  { value: 'filling', label: 'Filling' },
  { value: 'extraction', label: 'Extraction' },
  { value: 'root_canal', label: 'Root Canal' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'xray', label: 'X-Ray' },
  { value: 'other', label: 'Other' },
];

const DURATIONS = [
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '45', label: '45 min' },
  { value: '60', label: '60 min' },
  { value: '90', label: '90 min' },
  { value: '120', label: '120 min' },
];

export function BookAppointmentDialog({ open, onClose, onBook, defaultDate }: BookAppointmentDialogProps) {
  const { data: patients } = usePatients();
  const [form, setForm] = useState({
    patientId: '',
    type: '',
    date: defaultDate ? defaultDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    time: '09:00',
    duration: '30',
    notes: '',
    status: 'scheduled' as Appointment['status'],
    practitionerId: 'dr1',
  });

  const handleSubmit = () => {
    if (!form.patientId || !form.type || !form.date || !form.time) return;
    const scheduledAt = new Date(`${form.date}T${form.time}:00`).toISOString();
    onBook({
      clinicId: DEMO_CLINIC_ID,
      patientId: form.patientId,
      practitionerId: form.practitionerId,
      status: form.status,
      scheduledAt,
      durationMinutes: parseInt(form.duration),
      type: APPOINTMENT_TYPES.find((t) => t.value === form.type)?.label ?? form.type,
      notes: form.notes,
    });
    onClose();
    setForm({ patientId: '', type: '', date: new Date().toISOString().split('T')[0], time: '09:00', duration: '30', notes: '', status: 'scheduled', practitionerId: 'dr1' });
  };

  const patientOptions = (patients ?? []).map((p) => ({
    value: p.id,
    label: `${p.firstName} ${p.lastName}`,
  }));

  return (
    <Dialog open={open} onClose={onClose} className="max-w-md">
      <DialogHeader>
        <DialogTitle>Book Appointment</DialogTitle>
      </DialogHeader>
      <DialogContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="patient">Patient *</Label>
          <Select
            id="patient"
            options={[{ value: '', label: 'Select patient...' }, ...patientOptions]}
            value={form.patientId}
            onChange={(e) => setForm((f) => ({ ...f, patientId: e.target.value }))}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="type">Appointment Type *</Label>
          <Select
            id="type"
            options={[{ value: '', label: 'Select type...' }, ...APPOINTMENT_TYPES]}
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="date">Date *</Label>
            <Input
              id="date"
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="time">Time *</Label>
            <Input
              id="time"
              type="time"
              value={form.time}
              onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="duration">Duration</Label>
            <Select
              id="duration"
              options={DURATIONS}
              value={form.duration}
              onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            placeholder="Clinical notes or special instructions..."
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={!form.patientId || !form.type || !form.date || !form.time}
        >
          Book Appointment
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
